import React, { ReactNode } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';
import { UserRole } from '../types';

interface RoleGuardProps {
  children: ReactNode;
  permissions?: Permission[];
  roles?: UserRole[];
  requireAll?: boolean;
  fallback?: ReactNode;
}

/**
 * Component that conditionally renders children based on user permissions or roles
 * 
 * Usage:
 * <RoleGuard permissions={[Permission.CREATE_USERS]}>
 *   <button>Add User</button>
 * </RoleGuard>
 * 
 * <RoleGuard roles={[UserRole.CLIENT_ADMIN, UserRole.SUPER_ADMIN]}>
 *   <AdminPanel />
 * </RoleGuard>
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  permissions,
  roles,
  requireAll = false,
  fallback = null
}) => {
  const { can, canAny, canAll, isAnyRole } = usePermissions();

  let hasAccess = true;

  // Check role-based access
  if (roles && roles.length > 0) {
    hasAccess = isAnyRole(roles);
  }

  // Check permission-based access
  if (hasAccess && permissions && permissions.length > 0) {
    hasAccess = requireAll ? canAll(permissions) : canAny(permissions);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default RoleGuard;





